// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the `satellites.*` commands: the AYNI chatbot answers questions on
 * the satellites ↔ catalog graph and acts on it (AYNI 3D's
 * `docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md` §4.5; the brief is
 * `AYNI-VR/handoff-chatbot-satellites.md`, the plan
 * `docs/AYNI_XR_SATELLITE_CATALOG_PLAN.md`).
 *
 * Everything is read from the Satellites layer itself (`SatelliteLayer`:
 * its sources, its `sources.json` index, its settings, passes and events)
 * and from the dataset's DSA 1.10 platforms, through the layer's own
 * helpers (`sourcesAt`, `swathOf`, `horizonRadiusKm`, `bandsAt`); nothing
 * here keeps a copy of the data. Actions go through the layer's API too
 * (`setOn`, `update`, `selectNorad`, `focusEvent`), never around it.
 *
 * Outcomes are English (the model answers in the visitor's language);
 * the layer's notes and windows come in the page's language, as the cards
 * show them.
 */
import type { AgentCommand, AgentToolOutcome } from '../ayni3d/shared/protocol'
import type { Dataset } from '../types'
import type { SatelliteLayer } from '../services/satellites/satelliteLayer'
import type { SatelliteDatasetRow } from '../services/satellites/satelliteText'
import { indexDatasetId, localised } from '../services/satellites/satelliteText'
import type { SatelliteEvent, SourcePlatformEntry } from '../services/satellites/satelliteTypes'
import { platformName, sourcesAt, type DsaPlatform } from '../services/satellites/sourcePlatforms'
import { horizonRadiusKm, swathOf } from '../services/satellites/footprint'
import { bandsAt, type TimelineBand } from '../services/timelineTrackCanvas'
import { ok, unavailable } from './xrChatOutcomes'

/** The parts of the layer the commands read and drive (`SatelliteLayer` is one). */
export type XrChatSatelliteLayer = Pick<
  SatelliteLayer,
  | 'settings'
  | 'status'
  | 'onEarth'
  | 'sources'
  | 'sourcesIndex'
  | 'loadSourcesIndex'
  | 'platformOf'
  | 'satellitesOfDataset'
  | 'selectNorad'
  | 'selectedInfo'
  | 'setOn'
  | 'update'
  | 'onChange'
  | 'skyOn'
  | 'skyAvailable'
  | 'skyPasses'
  | 'events'
  | 'focusEvent'
> & { engine: Pick<SatelliteLayer['engine'], 'infos'> }

/** What the executor needs for the satellites commands (`main.ts` builds it on the layer). */
export interface XrChatSatellites {
  layer: XrChatSatelliteLayer
  /** The datasets a satellite feeds, as its card lists them (`satelliteDatasets`). */
  datasetRows(norad: number): SatelliteDatasetRow[]
  /** Why a satellite is not drawn ("Retired 2018; …"), null for a tracked one (`platformNote`). */
  note(norad: number): string | null
  /** The dataset on screen's DSA 1.10 platforms (empty without them). */
  platforms(): readonly DsaPlatform[]
  /** The mission bands on its date track (`platformBands`). */
  bands(): readonly TimelineBand[]
}

/** What the satellites commands need from the rest of the page. */
export interface XrChatSatelliteContext {
  dataset: Dataset | null
  datasets: () => readonly Dataset[]
  /** The frame on screen's instant, epoch ms, or null. */
  frameMs: number | null
  /** The dataset's first and last frame, epoch ms, when it has a time axis. */
  axis: { startMs: number; endMs: number } | null
  /** The package a dataset needs that the plan lacks, for people; null when it may load. */
  streamLock?: (dataset: Dataset) => Promise<string | null>
  language: string
}

type Command<T extends string> = Extract<AgentCommand, { type: T }>

/** At most this many satellites per `satellites.datasets` answer (a mission like GOES has twenty). */
export const MAX_SATELLITE_MATCHES = 8
const DEFAULT_EVENTS = 5
/** How long `satellites.show` waits for the layer's orbits to arrive. */
const SHOW_WAIT_MS = 8000
const TEXT_CHARS = 200
const GEO_RADIUS_KM = 42_164

export const SATELLITES_METHOD =
  "Read from the Satellites layer: the dataset's DSA 1.10 platforms (which satellites, with their periods) and the stream host's sources.json (which datasets each satellite feeds, and which are still tracked)."
const APPROXIMATE = {
  code: 'approximate',
  text: 'Satellite positions come from mean orbital elements and are approximate: never for collision or safety warnings.',
}

const iso = (ms: number | null): string | null => (ms === null || !Number.isFinite(ms) ? null : new Date(ms).toISOString().slice(0, 10))

function clip(text: string | undefined, max = TEXT_CHARS): string | undefined {
  if (!text) return undefined
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat
}

/** A name for matching: no case, accents, spaces or hyphens ("Sentinel-1A" → "sentinel1a"). */
export function nameKey(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[\s\-_.]+/g, '')
}

/** Every name a satellite of the index goes by; "GOES-19 (GOES-East)" is also "GOES-19" and "GOES-East". */
function namesOf(p: SourcePlatformEntry): string[] {
  const names = typeof p.name === 'string' ? [p.name] : Object.values(p.name)
  if (p.satcatName) names.push(p.satcatName)
  return names.flatMap((n) => {
    const m = /^(.+?)\s*\((.+)\)\s*$/.exec(n)
    return m ? [n, m[1], m[2]] : [n]
  })
}

const indexName = (p: SourcePlatformEntry, lang: string): string => (typeof p.name === 'string' ? p.name : p.name[lang] ?? p.name.en ?? Object.values(p.name)[0] ?? `NORAD ${p.norad}`)

/** A period as text: "1975-10-16 to 1978-07-01", "from 2018-05-22", or null for the whole dataset. */
function period(fromMs: number | null, toMs: number | null): string | null {
  if (fromMs === null && toMs === null) return null
  if (fromMs !== null && toMs !== null) return `${iso(fromMs)} to ${iso(toMs)}`
  return fromMs !== null ? `from ${iso(fromMs)}` : `until ${iso(toMs)}`
}

const ms = (value: string | undefined | null): number | null => {
  const t = value ? Date.parse(value) : NaN
  return Number.isFinite(t) ? t : null
}

/** Is this satellite's orbit in the data (drawable)? The index says; else whether the layer holds it. */
function trackedOf(sats: XrChatSatellites, norad: number): boolean {
  const entry = sats.layer.platformOf(norad)
  if (entry) return entry.tracked
  return sats.layer.engine.infos.some((i) => i.norad === norad)
}

/**
 * What a satellite sees, as the field of view draws it (`footprint.ts`): an
 * instrument with a known swath sees a strip that wide; a geostationary
 * imager sees its whole disk out to the horizon; an instrument that images
 * nothing on the ground sees nothing there.
 */
export function seesOf(instrument: string | null | undefined, distanceKm: number | null): Record<string, unknown> | null {
  const swath = swathOf(instrument)
  if (swath === 0) return { what: 'nothing on the ground (its instrument does not image the Earth)' }
  const horizon = distanceKm === null ? null : Math.round(horizonRadiusKm(distanceKm))
  if (swath !== null) {
    const half = horizon === null ? swath / 2 : Math.min(swath / 2, horizon)
    return { what: 'a strip under its track', swathKm: swath, radiusKm: Math.round(half), instrument }
  }
  if (horizon === null) return null
  return { what: 'everything out to its horizon (the most it could see; the instrument may see less)', radiusKm: horizon }
}

/** How far from the Earth's centre a satellite of the layer is (its orbit's semi-major axis). */
function distanceOf(sats: XrChatSatellites, norad: number, platform: DsaPlatform | null): number | null {
  const info = sats.layer.engine.infos.find((i) => i.norad === norad)
  if (info) return info.semiMajorKm
  // An old frame's geostationary source is drawn at its nominal slot.
  return platform?.nominalLongitude !== null && platform?.nominalLongitude !== undefined ? GEO_RADIUS_KM : null
}

/** `satellites.sources`: the satellites the dataset on screen comes from. */
export async function satellitesSources(sats: XrChatSatellites, ctx: XrChatSatelliteContext, command: Command<'satellites.sources'>): Promise<AgentToolOutcome> {
  const dataset = ctx.dataset
  if (!dataset) return unavailable('no-dataset', 'There is no dataset on screen.')
  const layer = sats.layer
  await layer.loadSourcesIndex()
  const lang = ctx.language
  const platforms = sats.platforms().filter((p) => p.kind === 'satellite' && p.norad !== null)
  const indexId = indexDatasetId(dataset.id)
  const indexed = indexId ? layer.satellitesOfDataset(indexId) : []
  if (!platforms.length && !indexed.length && !layer.sources.length) {
    return unavailable('no-platforms', 'This dataset does not name the satellites it is built from.')
  }
  const on = layer.settings.on && layer.onEarth
  const values: Record<string, unknown> = { dataset: dataset.title }
  const cautions = [APPROXIMATE]
  // The frame shown: the layer's own sources (the data on the globes, its overlays too).
  if (command.date === undefined) {
    values.at = iso(ctx.frameMs)
    values.now = layer.sources.map((s) => {
      const tracked = trackedOf(sats, s.norad)
      const drawn = on && s.index >= 0 && !s.standby && !s.dated
      const entry: Record<string, unknown> = { name: platformName(s.platform, lang), norad: s.norad, role: s.platform.role, ...(s.platform.instrument ? { instrument: s.platform.instrument } : {}), drawn }
      if (s.standby) entry.standIn = 'a stand-in, held back while the main satellite is in use'
      else if (!tracked) entry.notTracked = sats.note(s.norad) ?? 'not in the tracked orbits, so not drawn'
      else if (s.dated) {
        entry.datedFrame = s.platform.nominalLongitude !== null
          ? `the frame is days from the orbits' date: drawn at its nominal longitude (${s.platform.nominalLongitude}°), not where it is today`
          : "the frame is days from the orbits' date, so it is named, not drawn at today's position"
      } else if (!on) entry.layerOff = 'the Satellites layer is off; satellites_show draws it'
      if (!s.standby && tracked) {
        const sees = seesOf(s.platform.instrument, distanceOf(sats, s.norad, s.platform))
        if (sees) entry.sees = sees
      }
      return entry
    })
  } else {
    const atMs = Date.parse(command.date)
    values.date = iso(atMs)
    values.ofDate = sourcesAt(platforms, atMs).map(({ platform, standby }) => ({
      name: platformName(platform, lang),
      norad: platform.norad,
      ...(standby ? { standIn: true } : {}),
      period: period(platform.fromMs, platform.toMs),
    }))
    if (bandsAt(sats.bands(), atMs).some((b) => b.approximate)) {
      values.approximate = true
      cautions.push({ code: 'wholeYears', text: 'These periods are whole years that overlap: say "about" for who made a given year.' })
    }
    if (ctx.axis && (atMs < ctx.axis.startMs || atMs > ctx.axis.endMs)) {
      cautions.push({ code: 'outside', text: `The dataset runs from ${iso(ctx.axis.startMs)} to ${iso(ctx.axis.endMs)}; that date has no frame.` })
    }
  }
  values.fieldOfView = on && layer.settings.fov
  const all = allSatellites(sats, platforms, indexed, indexId, lang)
  values.all = all
  // GOES generations names 22 satellites: past the result's limit, each becomes one line, then the list is cut.
  if (sizeOf(values) > RESULT_BUDGET) values.all = all.map(lineOf)
  while (sizeOf(values) > RESULT_BUDGET && (values.all as unknown[]).length > 1) {
    const list = values.all as unknown[]
    values.all = list.slice(0, Math.ceil(list.length * 0.75))
    values.allCount = all.length
  }
  return ok(values, SATELLITES_METHOD, cautions)
}

/**
 * The room a result's values may take: the engine cuts a tool result at
 * 3,000 characters, and the method, cautions and envelope need the rest.
 */
const RESULT_BUDGET = 2200
const sizeOf = (values: Record<string, unknown>): number => JSON.stringify(values).length

/** A satellite of `all` as one line: "GOES-7 (NORAD 17561; 1987–1989, 1992–1994; Retired 2012…)". */
function lineOf(row: Record<string, unknown>): string {
  const years = typeof row.period === 'string' ? row.period.replace(/(\d{4})-\d\d-\d\d/g, '$1').replace(/ to /g, '–') : null
  const parts = [`NORAD ${String(row.norad)}`, years, row.tracked ? 'tracked' : (row.note as string | undefined) ?? 'not tracked'].filter(Boolean)
  return `${String(row.name)} (${parts.join('; ')})`
}

/** Every satellite of the dataset, its periods joined: from the descriptor, else the index. */
function allSatellites(sats: XrChatSatellites, platforms: readonly DsaPlatform[], indexed: readonly SourcePlatformEntry[], indexId: string | null, lang: string): Record<string, unknown>[] {
  const rows = new Map<number, { name: string; periods: string[] }>()
  const add = (norad: number, name: string, p: string | null) => {
    const row = rows.get(norad) ?? { name, periods: [] }
    if (p && !row.periods.includes(p)) row.periods.push(p)
    rows.set(norad, row)
  }
  for (const p of platforms) add(p.norad!, platformName(p, lang), period(p.fromMs, p.toMs))
  for (const p of indexed) {
    for (const d of p.datasets) if (d.id === indexId) add(p.norad, indexName(p, lang), period(ms(d.from), ms(d.to)))
    if (!rows.has(p.norad)) add(p.norad, indexName(p, lang), null)
  }
  return [...rows].map(([norad, row]) => {
    const tracked = trackedOf(sats, norad)
    const note = tracked ? null : sats.note(norad)
    return { name: row.name, norad, tracked, ...(row.periods.length ? { period: row.periods.join(', ') } : {}), ...(note ? { note } : {}) }
  })
}

/** The satellites of the index a name stands for: exact names first, then a mission's (prefix), then any containing it. */
export function matchSatellites(index: Record<string, SourcePlatformEntry>, query: string): SourcePlatformEntry[] {
  const all = Object.values(index).filter((p): p is SourcePlatformEntry => !!p && typeof p.norad === 'number')
  if (/^\d+$/.test(query.trim())) return all.filter((p) => p.norad === Number(query.trim()))
  const q = nameKey(query)
  if (!q) return []
  const by = (test: (key: string) => boolean) => all.filter((p) => namesOf(p).some((n) => test(nameKey(n))))
  let found = by((k) => k === q)
  if (!found.length) found = by((k) => k.startsWith(q))
  if (!found.length) found = by((k) => k.includes(q))
  return found.sort((a, b) => indexName(a, 'en').localeCompare(indexName(b, 'en'), 'en', { numeric: true }))
}

/** `satellites.datasets`: the datasets a satellite (or a mission's satellites) feeds. */
export async function satellitesDatasets(sats: XrChatSatellites, ctx: XrChatSatelliteContext, command: Command<'satellites.datasets'>): Promise<AgentToolOutcome> {
  const index = await sats.layer.loadSourcesIndex()
  if (!index) return unavailable('no-index', 'The list of which satellites feed which datasets could not be read just now.')
  const found = matchSatellites(index.platforms, command.satellite)
  if (!found.length) return unavailable('no-match', `No satellite named "${command.satellite}" feeds a dataset in the catalogue.`)
  const byId = new Map(ctx.datasets().map((d) => [d.id, d]))
  const satellites = []
  for (const p of found.slice(0, MAX_SATELLITE_MATCHES)) {
    const datasets = []
    for (const r of sats.datasetRows(p.norad)) {
      const ds = byId.get(r.id)
      const lockedBy = ds && ctx.streamLock ? await ctx.streamLock(ds) : null
      datasets.push({
        id: r.id,
        title: r.title,
        ...(r.window ? { period: r.window } : {}),
        ...(r.standIn ? { standIn: true } : {}),
        ...(r.body ? { shows: r.body } : {}),
        ...(lockedBy ? { locked: `needs the ${lockedBy} package` } : {}),
        ...(ds ? {} : { inCatalogue: false }),
      })
    }
    const note = p.tracked ? null : sats.note(p.norad)
    satellites.push({
      name: indexName(p, ctx.language),
      norad: p.norad,
      tracked: p.tracked,
      ...(note ? { note } : {}),
      ...(p.launch ? { launch: p.launch.slice(0, 10) } : {}),
      ...(p.decay ? { decay: p.decay.slice(0, 10) } : {}),
      datasets,
    })
  }
  const values: Record<string, unknown> = { satellites }
  // A mission's satellites past the result's limit: fewer of them, and how many more there are.
  while (sizeOf(values) > RESULT_BUDGET && satellites.length > 1) satellites.pop()
  if (found.length > satellites.length) {
    const rest = found.slice(satellites.length).map((p) => indexName(p, ctx.language))
    values.more = `${rest.length} more satellites match (${rest.join(', ')}); ask for one by name.`
  }
  return ok(values, SATELLITES_METHOD)
}

/** Waits until the layer holds its orbits (or failed), at most `SHOW_WAIT_MS`. */
function whenReady(layer: XrChatSatelliteLayer, timeoutMs: number): Promise<void> {
  if (layer.status === 'ready' || layer.status === 'error') return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      off()
      resolve()
    }
    const timer = setTimeout(done, timeoutMs)
    const off = layer.onChange(() => {
      if (layer.status === 'ready' || layer.status === 'error' || !layer.settings.on) done()
    })
  })
}

/** `satellites.show`: the layer on or off, a satellite selected, the field of view. */
export async function satellitesShow(sats: XrChatSatellites, command: Command<'satellites.show'>, waitMs = SHOW_WAIT_MS): Promise<AgentToolOutcome> {
  const layer = sats.layer
  if (command.on === false) {
    if (layer.settings.on) layer.setOn(false)
    return ok({ on: false }, 'The Satellites layer, as its switch in Tools.')
  }
  if (!layer.onEarth) return unavailable('off-earth', 'The globe shows another body: satellites orbit the Earth, so the layer waits until an Earth dataset is back.')
  const norad = command.norad
  // A satellite whose orbit is not in the data is named, not drawn: change nothing.
  const note = norad === undefined ? null : sats.note(norad)
  if (note) return unavailable('not-tracked', `NORAD ${norad}: ${note}`)
  if (command.fov !== undefined && command.fov !== layer.settings.fov) layer.update({ fov: command.fov })
  if (!layer.settings.on) layer.setOn(true)
  const values: Record<string, unknown> = { on: true }
  if (norad !== undefined) {
    let selected = layer.selectNorad(norad)
    if (!selected) {
      await whenReady(layer, waitMs)
      selected = layer.selectNorad(norad)
    }
    if (!selected) return unavailable('not-tracked', `NORAD ${norad} is not among the orbits the layer holds, so it cannot be shown.`)
    const info = layer.selectedInfo
    values.selected = { name: info?.name ?? `NORAD ${norad}`, norad }
  }
  values.fieldOfView = layer.settings.fov
  return ok(values, 'The Satellites layer, as its switch, its card and its Field of view switch in Tools.', [APPROXIMATE])
}

/** `satellites.passes`: the catalog's satellites passing over the visitor (Sky above me). */
export function satellitesPasses(sats: XrChatSatellites, ctx: XrChatSatelliteContext): AgentToolOutcome {
  const layer = sats.layer
  if (!layer.skyOn) {
    return unavailable('no-sky', layer.skyAvailable
      ? 'Passes over the visitor need Sky above me, which is off: it is in the Satellites card in AR.'
      : 'Passes over the visitor need Sky above me, which runs only in AR, on a phone or headset.')
  }
  const titles = new Map(ctx.datasets().map((d) => [d.id, d.title]))
  return ok(
    {
      passes: layer.skyPasses.map((p) => ({
        name: p.name,
        norad: p.norad,
        overheadNow: p.overhead,
        rises: new Date(p.riseMs).toISOString().replace(/:\d\d\.\d{3}Z$/, 'Z'),
        maxElevationDeg: Math.round(p.maxElDeg),
        datasets: p.datasets.map((id) => ({ id, title: titles.get(id) ?? id })),
      })),
      ...(layer.skyPasses.length ? {} : { none: 'None of the catalogue\'s satellites passes over the visitor in the coming day.' }),
    },
    "Worked out by Sky above me from the visitor's place, every ten minutes, for the satellites that feed the catalogue's datasets: over them now (above 10°) or their next rise in the coming day.",
    [APPROXIMATE],
  )
}

/** One event as the chatbot reads it, with the datasets its satellites feed. */
function eventEntry(sats: XrChatSatellites, e: SatelliteEvent, lang: string): Record<string, unknown> {
  const datasets: Array<{ id: string; title: string }> = []
  for (const n of e.norad ?? []) for (const r of sats.datasetRows(n)) if (!datasets.some((d) => d.id === r.id)) datasets.push({ id: r.id, title: r.title })
  return {
    id: e.id,
    kind: e.kind,
    date: e.date,
    title: localised(e.title, lang),
    ...(e.text ? { text: clip(localised(e.text, lang)) } : {}),
    ...(e.norad?.length ? { norad: e.norad } : {}),
    ...(datasets.length ? { datasets: datasets.slice(0, 3) } : {}),
  }
}

/** `satellites.events`: the space events, newest first; `open` shows one on the globe. */
export function satellitesEvents(sats: XrChatSatellites, ctx: XrChatSatelliteContext, command: Command<'satellites.events'>): AgentToolOutcome {
  const layer = sats.layer
  const events = layer.events
  if (!events.length) {
    return unavailable('no-event', layer.status === 'ready' ? 'There are no space events in the data just now.' : 'The space events load with the Satellites layer: turn it on with satellites_show first.')
  }
  if (command.open !== undefined) {
    const e = events.find((x) => x.id === command.open)
    if (!e) return unavailable('no-event', `No space event has the id ${command.open}; list them with satellites_events first.`)
    if (!layer.onEarth) return unavailable('off-earth', 'The globe shows another body: space events are shown on the Earth.')
    layer.focusEvent(e)
    return ok({ opened: eventEntry(sats, e, ctx.language) }, 'Opened as its row in the Satellites card does: its satellite selected, or its card and last orbit.', [APPROXIMATE])
  }
  const limit = command.limit ?? DEFAULT_EVENTS
  return ok({ events: events.slice(0, limit).map((e) => eventEntry(sats, e, ctx.language)), total: events.length }, "The Satellites layer's space events (the stream host's events.json), newest first.")
}

/** One line for the turn's context when the data on screen names its satellites, so the model knows to ask. */
export function satellitesSituation(sats: XrChatSatellites | null, lang: string): string | null {
  if (!sats) return null
  const names = [...new Set(sats.layer.sources.map((s) => platformName(s.platform, lang)))]
  if (!names.length) return null
  const shown = names.length > 6 ? `${names.slice(0, 6).join(', ')} and ${names.length - 6} more` : names.join(', ')
  return `The data on screen comes from satellites (${shown}); satellites_sources says which are drawn and what they see.`
}
